/**
 * RevenueCat React Hooks
 *
 * Custom hooks for using RevenueCat in React components
 */

import { useState, useEffect, useCallback } from 'react';
import { CustomerInfo, PurchasesPackage, PurchasesStoreProduct } from 'react-native-purchases';

import { isScreenshotMode, DEMO_STORE_PRODUCTS } from '../../../../lib/screenshotMode';
import {
  revenueCatService,
  RevenueCatOffering,
  RevenueCatPackageInfo,
  RevenueCatSubscriptionInfo,
  PurchaseResult,
  ENTITLEMENTS
} from '../services/revenueCatService';
import {
  applyStorefrontPricingToOffering,
  applyStorefrontPricingToProducts,
} from '../config/storefrontPrices';

const mergeStoreProductDetails = (
  offering: RevenueCatOffering | null,
  products: PurchasesStoreProduct[],
): RevenueCatOffering | null => {
  if (!offering || products.length === 0) {
    return offering;
  }

  const productsById = new Map(products.map((product) => [product.identifier, product]));

  return {
    ...offering,
    packages: offering.packages.map((pkg: RevenueCatPackageInfo) => {
      const storeProduct = productsById.get(pkg.product.identifier);
      if (!storeProduct) {
        return pkg;
      }

      return {
        ...pkg,
        product: {
          ...pkg.product,
          title: storeProduct.title || pkg.product.title,
          description: storeProduct.description || pkg.product.description,
          price: storeProduct.price,
          priceString: storeProduct.priceString,
          currencyCode: storeProduct.currencyCode,
          introPrice: storeProduct.introPrice
            ? {
                price: storeProduct.introPrice.price,
                priceString: storeProduct.introPrice.priceString,
                period: storeProduct.introPrice.period,
              }
            : pkg.product.introPrice,
        },
      };
    }),
  };
};

/**
 * Hook to get current subscription status
 * Auto-updates when subscription changes
 */
export function useSubscriptionStatus() {
  const [subscriptionInfo, setSubscriptionInfo] = useState<RevenueCatSubscriptionInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    // Store screenshot capture: no store connection — treat as not subscribed.
    if (isScreenshotMode()) {
      setSubscriptionInfo(null);
      setError(null);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const info = await revenueCatService.getSubscriptionInfo();
      setSubscriptionInfo(info);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load subscription');
      setSubscriptionInfo(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return {
    subscriptionInfo,
    loading,
    error,
    refresh,
    isActive: subscriptionInfo?.isActive || false,
    tier: subscriptionInfo?.tier || null
  };
}

/**
 * Hook to get available offerings
 */
export function useOfferings() {
  const [currentOffering, setCurrentOffering] = useState<RevenueCatOffering | null>(null);
  const [fallbackProducts, setFallbackProducts] = useState<PurchasesStoreProduct[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    // Store screenshot capture: RevenueCat is never configured in screenshot
    // mode, so serve demo products instead of hitting the store.
    if (isScreenshotMode()) {
      setFallbackProducts(DEMO_STORE_PRODUCTS as unknown as PurchasesStoreProduct[]);
      setCurrentOffering(null);
      setError(null);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      setFallbackProducts(null);
      const offerings = await revenueCatService.getOfferings();
      const [products, storefrontCountryCode] = await Promise.all([
        revenueCatService.getFallbackProducts(),
        revenueCatService.getStorefrontCountryCode(),
      ]);
      const pricedProducts = applyStorefrontPricingToProducts(products || [], storefrontCountryCode);
      const currentOffering = applyStorefrontPricingToOffering(
        mergeStoreProductDetails(offerings.current, pricedProducts),
        storefrontCountryCode,
      );

      setCurrentOffering(currentOffering);

      const currentHasPackages = !!currentOffering?.packages?.length;
      if (!currentHasPackages) {
        if (pricedProducts && pricedProducts.length > 0) {
          setFallbackProducts(pricedProducts);
        }
      }
    } catch (err) {
      // If Offerings are unavailable (common setup issue during review), fall back to StoreKit products.
      const products = await revenueCatService.getFallbackProducts();
      if (products && products.length > 0) {
        const storefrontCountryCode = await revenueCatService.getStorefrontCountryCode();
        setFallbackProducts(applyStorefrontPricingToProducts(products, storefrontCountryCode));
        setCurrentOffering(null);
        setError(null);
      } else {
        setError(err instanceof Error ? err.message : 'Failed to load offerings');
        setFallbackProducts(null);
        setCurrentOffering(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return {
    currentOffering,
    fallbackProducts,
    loading,
    error,
    refresh
  };
}

/**
 * Hook to handle purchases
 */
export function usePurchase() {
  const [purchasing, setPurchasing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const purchase = useCallback(async (pkg: PurchasesPackage): Promise<PurchaseResult> => {
    try {
      setPurchasing(true);
      setError(null);
      const result = await revenueCatService.purchasePackage(pkg);

      if (!result.success && !result.userCancelled) {
        setError(result.error || 'Purchase failed');
      }

      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Purchase failed';
      setError(errorMessage);
      return {
        success: false,
        error: errorMessage
      };
    } finally {
      setPurchasing(false);
    }
  }, []);

  return {
    purchase,
    purchasing,
    error,
    clearError: () => setError(null)
  };
}

/**
 * Hook to handle restore purchases
 */
export function useRestorePurchases() {
  const [restoring, setRestoring] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const restore = useCallback(async (): Promise<PurchaseResult> => {
    try {
      setRestoring(true);
      setError(null);
      const result = await revenueCatService.restorePurchases();

      if (!result.success) {
        setError(result.error || 'Restore failed');
      }

      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Restore failed';
      setError(errorMessage);
      return {
        success: false,
        error: errorMessage
      };
    } finally {
      setRestoring(false);
    }
  }, []);

  return {
    restore,
    restoring,
    error,
    clearError: () => setError(null)
  };
}

/**
 * Hook to check specific entitlement
 */
export function useEntitlement(entitlement: string) {
  const [hasEntitlement, setHasEntitlement] = useState(false);
  const [loading, setLoading] = useState(true);

  const check = useCallback(async () => {
    try {
      setLoading(true);
      const result = await revenueCatService.hasEntitlement(entitlement);
      setHasEntitlement(result);
    } catch (err) {
      console.error('Failed to check entitlement:', err);
      setHasEntitlement(false);
    } finally {
      setLoading(false);
    }
  }, [entitlement]);

  useEffect(() => {
    check();
  }, [check]);

  return {
    hasEntitlement,
    loading,
    refresh: check
  };
}

/**
 * Hook to get customer info
 */
export function useCustomerInfo() {
  const [customerInfo, setCustomerInfo] = useState<CustomerInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const info = await revenueCatService.getCustomerInfo();
      setCustomerInfo(info);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load customer info');
      setCustomerInfo(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return {
    customerInfo,
    loading,
    error,
    refresh
  };
}

/**
 * Convenience hook for premium features
 */
export function usePremiumFeatures() {
  const { hasEntitlement: hasPremium, loading: premiumLoading } = useEntitlement(ENTITLEMENTS.PREMIUM);

  return {
    hasPremium,
    hasFamily: hasPremium,
    hasGroup: false,
    loading: premiumLoading
  };
}

/**
 * Hook for initializing RevenueCat with user
 */
export function useRevenueCatInit(userId?: string) {
  const [initialized, setInitialized] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const init = async () => {
      try {
        if (userId) {
          await revenueCatService.identifyUser(userId);
        } else {
          await revenueCatService.initialize();
        }
        setInitialized(true);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Initialization failed');
        setInitialized(false);
      }
    };

    init();
  }, [userId]);

  return {
    initialized,
    error
  };
}
